import { useNavigate } from 'react-router-dom';

import { Employer, CareerPathResponse } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';

import { Box, Typography, Chip, SxProps, Theme } from '@mui/material';

import { useAgentContext } from './CareerPathSection.agentContext';

type CareerPathSectionProps = Readonly<{
  employer: Employer;
}>;

const groupAndSortCareerPaths = (careerPaths: CareerPathResponse[]) => {
  const grouped = careerPaths.reduce(
    (acc, careerPath) => {
      const categoryName =
        careerPath.career_category?.name || `Category ${careerPath.career_category_id}`;

      if (!acc[categoryName]) {
        acc[categoryName] = [];
      }

      acc[categoryName].push(careerPath);

      return acc;
    },
    {} as Record<string, typeof careerPaths>,
  );

  return Object.entries(grouped).sort(
    ([, careerPathsA], [, careerPathsB]) => careerPathsB.length - careerPathsA.length,
  );
};

export const CareerPathSection = ({ employer }: CareerPathSectionProps) => {
  useAgentContext(employer.career_paths);
  const navigate = useNavigate();

  const sortedGroupedCareerPaths = groupAndSortCareerPaths(employer.career_paths);

  return (
    <Box sx={categoriesGridSx}>
      {sortedGroupedCareerPaths.map(([categoryName, careerPaths]) => (
        <Box key={categoryName}>
          <Typography variant="text-md-semibold" sx={categoryTitleSx}>
            {categoryName}
          </Typography>
          <Box sx={chipsContainerSx}>
            {careerPaths.map((careerPath) => {
              return (
                <Chip
                  id={`employer-career-path-chip-${careerPath.id}`}
                  key={careerPath.id}
                  size="sm"
                  label={careerPath.name}
                  variant="outlined"
                  clickable
                  sx={chipSx}
                  onClick={() => navigate(`${APP_ROUTE.CAREER_PATHS}/${careerPath.id}`)}
                />
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const categoriesGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-2xl'],
  alignItems: 'flex-start',
  '@container (min-width: 500px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  '@container (min-width: 750px)': {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
});

const categoryTitleSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-2xl'],
  color: backrs.colors['Colors/Foreground/fg-brand-secondary'],
});

const chipsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  alignItems: 'flex-start',
});

const chipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  height: 'auto',
  '& .MuiChip-label': {
    ...typography['text-sm-medium'],
    color: backrs.colors['Gray/700'],
    paddingTop: backrs.spacing['spacing-xxs'],
    paddingBottom: backrs.spacing['spacing-xxs'],
    paddingLeft: backrs.spacing['spacing-md'],
    paddingRight: backrs.spacing['spacing-md'],
  },
});
